/* PathStories — simple Google-review style wall.
   Real reviews only. POSTs to /api/stories — one review per device. */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Star, ShieldCheck, CheckCircle2, AlertTriangle, Send, LogIn } from 'lucide-react';
import { useAuth } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One review per device — same id the guest session uses.
const deviceId = () => {
  try {
    let d = localStorage.getItem('lxt_device_id');
    if (!d) {
      d = window.crypto?.randomUUID?.() || `dev_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem('lxt_device_id', d);
    }
    return d;
  } catch { return ''; }
};
const deviceHeaders = () => ({ 'X-Device-Id': deviceId() });

const ROLES = [
  { v: 'student',      l: 'Student' },
  { v: 'parent',       l: 'Parent' },
  { v: 'family',       l: 'Family member' },
  { v: 'working',      l: 'Working professional' },
  { v: 'job-seeker',   l: 'Job seeker' },
  { v: 'blue-collar',  l: 'Blue-collar worker' },
  { v: 'entrepreneur', l: 'Entrepreneur' },
];
const ROLE_LABELS = Object.fromEntries(ROLES.map((r) => [r.v, r.l]));

const AVATAR_COLORS = ['#0D9488', '#7C3AED', '#DB2777', '#D97706', '#2563EB', '#059669', '#DC2626', '#4F46E5'];
const avatarColor = (name = '') => AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];
const displayName = (full = '') => {
  const p = full.trim().split(/\s+/);
  return p.length > 1 ? `${p[0]} ${p[p.length - 1][0]}.` : p[0] || 'Anonymous';
};
const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  const d = Math.floor(s / 86400);
  if (d < 30) return d === 1 ? 'a day ago' : `${d} days ago`;
  const m = Math.floor(d / 30);
  return m <= 1 ? 'a month ago' : `${m} months ago`;
};

const Stars = ({ value, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} className={n <= value ? 'text-amber-400' : 'text-gray-300'} fill={n <= value ? 'currentColor' : 'none'} strokeWidth={n <= value ? 0 : 1.6} />
    ))}
  </span>
);

const initial = { full_name: '', role: 'student', email: '', story: '', rating: 0 };

const FACES = ['🤔', '😞', '😕', '🙂', '😃', '🤩'];
const FACE_LABELS = ['', 'Not for me', 'Could be better', 'It was okay', 'Really helpful', 'Loved it!'];
const QUICK_TAGS = [
  'Cleared my confusion',
  'Helped choose my stream',
  'Good for parents too',
  'Honest, no fake promises',
  'The previews cleared my doubts',
];

const PathStories = () => {
  const { user } = useAuth();
  const [form, setForm] = useState(initial);
  const [hover, setHover] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [published, setPublished] = useState([]);
  const [lastPosted, setLastPosted] = useState(null);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stories/mine`, { headers: deviceHeaders() })
      .then(({ data }) => setAlreadyReviewed(!!data.has_reviewed))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/stories`, { params: { limit: 12 } })
      .then(({ data }) => setPublished(data.stories || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.name && !user.is_guest) setForm((f) => (f.full_name ? f : { ...f, full_name: user.name }));
  }, [user]);

  const avg = useMemo(() => {
    const rated = published.filter((s) => s.rating);
    if (!rated.length) return null;
    return (rated.reduce((a, s) => a + s.rating, 0) / rated.length).toFixed(1);
  }, [published]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.rating) { setError('Tap the stars to rate your experience.'); return; }
    if (form.story.trim().length < 10) { setError('Please write at least a line or two about your experience.'); return; }
    setBusy(true);
    try {
      const payload = { ...form, email: form.email.trim() || undefined, consent: true };
      const { data: created } = await axios.post(`${API}/stories`, payload, { withCredentials: true, headers: deviceHeaders() });
      setDone(true);
      setAlreadyReviewed(true);
      setLastPosted({ rating: form.rating, story: form.story.trim() });
      setForm(initial);
      if (created?.status === 'published' && created.rating >= 4) setPublished((prev) => [created, ...prev]);
    } catch (err) {
      if (err?.response?.status === 409) { setAlreadyReviewed(true); setBusy(false); return; }
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong. Try again.';
      setError(typeof msg === 'string' ? msg : 'Please check the fields and try again.');
    } finally {
      setBusy(false);
    }
  };

  const active = hover || form.rating;

  return (
    <section className="py-20 md:py-24 px-4 md:px-6 bg-slate-50" data-testid="path-stories-section">
      <style>{`
        @keyframes lxt-face-pop { 0% { transform: scale(0.5); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }
        @keyframes lxt-star-pop { 0% { transform: scale(0.6); } 60% { transform: scale(1.3); } 100% { transform: scale(1); } }
        @keyframes lxt-slide-in { 0% { opacity: 0; transform: translateY(12px); } 100% { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="text-xs font-semibold tracking-widest text-teal-700 uppercase mb-3">Reviews</div>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight tracking-tight" data-testid="path-stories-headline">
            Real students. Real parents.
            <br />
            <span className="text-teal-600">Real reviews.</span>
          </h2>
          {avg && (
            <div className="mt-5 flex items-center gap-3" data-testid="path-stories-avg">
              <span className="text-4xl font-extrabold text-gray-900">{avg}</span>
              <div>
                <Stars value={Math.round(avg)} size={18} />
                <p className="text-xs text-gray-500 mt-0.5">{published.length} review{published.length !== 1 ? 's' : ''} · from real visitors</p>
              </div>
            </div>
          )}
        </div>

        {/* One review per device — already submitted */}
        {!done && alreadyReviewed && (
          <div className="mt-10 bg-white rounded-2xl border-2 border-emerald-200 p-8 text-center max-w-2xl" data-testid="path-stories-already-reviewed">
            <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-3" />
            <h3 className="text-xl font-extrabold text-gray-900 mb-1.5">You've already shared your review — thank you!</h3>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              LearnixTa allows one review per device, so every rating on the wall comes from a different visitor.
            </p>
          </div>
        )}

        {/* Write a review — engaging, progressive Google style */}
        {!done && !alreadyReviewed && (
          <div className="mt-10 bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-7 max-w-2xl overflow-hidden" data-testid="path-stories-form-wrap">
            {/* Big reactive face */}
            <div className="flex items-center gap-4 mb-3">
              <div
                key={active}
                className="w-16 h-16 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center text-4xl select-none"
                style={{ animation: 'lxt-face-pop 0.35s ease' }}
                data-testid="story-face"
              >
                {FACES[active]}
              </div>
              <div>
                <p className="font-extrabold text-gray-900 text-base md:text-lg">
                  {active ? FACE_LABELS[active] : 'How was your experience?'}
                </p>
                <p className="text-xs text-gray-500">Tap a star — takes 30 seconds · goes live instantly</p>
              </div>
            </div>

            {/* Interactive stars */}
            <div className="flex items-center gap-1.5 mb-1" data-testid="story-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n} type="button" data-testid={`story-star-${n}`}
                  onClick={() => setForm({ ...form, rating: n })}
                  onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                  className="p-0.5 transition-transform duration-150 hover:scale-125 active:scale-95"
                  style={n <= form.rating ? { animation: 'lxt-star-pop 0.3s ease' } : undefined}
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                >
                  <Star size={36} className={n <= active ? 'text-amber-400' : 'text-gray-300'} fill={n <= active ? 'currentColor' : 'none'} strokeWidth={n <= active ? 0 : 1.5} />
                </button>
              ))}
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-xl p-3 my-3 text-sm text-rose-800" data-testid="path-stories-error">
                <AlertTriangle size={16} className="text-rose-600 mt-0.5 shrink-0" /> {error}
              </div>
            )}

            {/* Rest of the form slides in only after a star is tapped */}
            {false && (
              <div className="mt-4 bg-teal-50 border border-teal-200 rounded-xl p-4" style={{ animation: 'lxt-slide-in 0.4s ease' }} data-testid="story-signin-prompt">
                <p className="font-bold text-gray-900 text-sm">One second — getting your free session ready</p>
                <p className="text-xs text-gray-600 mt-0.5 mb-3">No sign-up needed. Refresh the page and your stars will be waiting.</p>
                <Link
                  to="/dashboard"
                  data-testid="story-signin-btn"
                  className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-extrabold px-6 py-2.5 rounded-full text-sm transition-colors"
                >
                  <LogIn size={15} /> Start exploring free
                </Link>
              </div>
            )}
            {form.rating > 0 && (
              <form onSubmit={submit} className="space-y-3 mt-4" style={{ animation: 'lxt-slide-in 0.4s ease' }} data-testid="story-form-body">
                {/* Quick-tap starter chips */}
                <div className="flex flex-wrap gap-2">
                  {QUICK_TAGS.map((t) => (
                    <button
                      key={t} type="button"
                      data-testid={`story-tag-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      onClick={() => setForm({ ...form, story: form.story ? `${form.story.replace(/\.?\s*$/, '. ')}${t}.` : `${t}.` })}
                      className="text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 rounded-full px-3 py-1.5 transition-colors active:scale-95"
                    >
                      + {t}
                    </button>
                  ))}
                </div>
                <textarea
                  required rows={3} maxLength={2000}
                  value={form.story}
                  onChange={(e) => setForm({ ...form, story: e.target.value })}
                  data-testid="story-text"
                  placeholder="Tap a chip above or write in your own words — what did LearnixTa help you figure out?"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-[15px] resize-none"
                />

                {/* Role as tappable pills */}
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">I am a…</p>
                  <div className="flex flex-wrap gap-2" data-testid="story-role">
                    {ROLES.map((r) => (
                      <button
                        key={r.v} type="button"
                        data-testid={`story-role-${r.v}`}
                        onClick={() => setForm({ ...form, role: r.v })}
                        className={`text-xs font-bold rounded-full px-3.5 py-2 border transition-all active:scale-95 ${form.role === r.v ? 'bg-teal-600 border-teal-600 text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-teal-400 hover:text-teal-700'}`}
                      >
                        {r.l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    required value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    data-testid="story-name" placeholder="Your name"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-[15px]"
                  />
                  <input
                    type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="story-email" placeholder="Email (optional — never shown)"
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white text-[15px]"
                  />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
                  <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
                    <ShieldCheck size={12} className="text-emerald-600 shrink-0" />
                    Shown as first name + last initial. We remove anything abusive or fake.
                  </p>
                  <button
                    type="submit" disabled={busy} data-testid="story-submit"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 disabled:opacity-60 text-white font-extrabold px-7 py-3 rounded-full transition-all hover:shadow-lg active:scale-95 shrink-0"
                  >
                    {busy ? 'Posting…' : <>Post review <Send size={14} /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Success */}
        {done && (
          <div className="mt-10 bg-white rounded-2xl border-2 border-emerald-200 p-8 text-center max-w-2xl" data-testid="path-stories-success">
            <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-3" />
            <h3 className="text-xl font-extrabold text-gray-900 mb-1.5">
              {lastPosted && lastPosted.rating >= 4 ? 'Thank you! Your review is live on the wall.' : 'Thank you for your honest feedback!'}
            </h3>
            <p className="text-sm text-gray-600 max-w-md mx-auto mb-4">
              {lastPosted && lastPosted.rating >= 4
                ? 'It just went live below — it will help the next student or parent decide with more clarity.'
                : 'Your feedback helps us improve LearnixTa for every student and parent.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {lastPosted && (
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${'⭐'.repeat(lastPosted.rating)} I just rated LearnixTa ${lastPosted.rating}/5!\n\n"${lastPosted.story}"\n\nHonest career guidance for students & parents — check your path too 👉 ${window.location.origin}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="story-whatsapp-share"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5A] text-white font-extrabold px-6 py-2.5 rounded-full text-sm transition-colors active:scale-95"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  Share on WhatsApp
                </a>
              )}
            </div>
            {lastPosted && (
              <p className="text-[11px] text-gray-400 mt-3">Sharing helps another student or parent find honest guidance.</p>
            )}
          </div>
        )}

        {/* Reviews wall */}
        {published.length > 0 && (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="path-stories-wall">
            {published.map((s) => (
              <div key={s.id} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm flex flex-col" data-testid={`path-story-${s.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-extrabold shrink-0" style={{ background: avatarColor(s.full_name) }}>
                    {(s.full_name || '?').trim()[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate">{displayName(s.full_name)}</p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {ROLE_LABELS[s.role] || s.role}{s.location ? ` · ${s.location}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2.5">
                  {s.rating ? <Stars value={s.rating} size={14} /> : null}
                  <span className="text-[11px] text-gray-500">{timeAgo(s.created_at)}</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mt-2 flex-1">{s.story}</p>
                <div className="mt-3 pt-2.5 border-t border-gray-100">
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700">
                    <ShieldCheck size={11} /> Real review
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PathStories;
